import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router'

import { Icon } from '@/components/icon'
import { Render } from '@/components/render-if'
import { SidebarProvider } from '@/components/ui'
import { NavMain, type NavItem } from '@/components/ui/sidebar/nav-main'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenuButton,
  SidebarRail,
} from '@/components/ui/sidebar/sidebar'
import { useAtendimentoAtivo } from '@/features/atendimento/shared/hooks/use-atendimento-ativo'
import { RegionalSwitcher } from '@/features/regionais/components/regional-switcher'
import { useAuth } from '@/hooks'
import { useAtendimento } from '@/hooks/use-atendimento'
import { DestructiveActionGuard } from '@/shared/destructive-action-guard'

export const AppSidebarBrasil = ({ ...props }: React.ComponentProps<typeof Sidebar>) => {
  const { t } = useTranslation(['common', 'login'])
  const { user, logout } = useAuth()
  const { atendimento } = useAtendimento()
  useAtendimentoAtivo(Boolean(user?.unidadeDeNegocioId))

  const navMainItems: NavItem[] = [
    {
      title: t('layouts.sidebar.empreendimentos'),
      url: '/empreendimentos',
      icon: 'building-2',
    },
    {
      title: t('layouts.sidebar.espelho_de_vendas'),
      url: '/espelho-de-vendas',
      icon: 'floor-plan',
    },
    {
      title: t('layouts.sidebar.historico'),
      url: '/historico',
      icon: 'scroll-text',
      children: [
        { title: t('layouts.sidebar.simulacoes'), url: '/historico/simulacoes' },
        { title: t('layouts.sidebar.propostas'), url: '/historico/propostas' },
      ],
    },
  ]

  const navMainAdminItems: NavItem[] = [
    {
      title: t('layouts.sidebar.configuracoes.titulo'),
      url: '/configuracoes',
      icon: 'settings',
      children: [
        { title: t('layouts.sidebar.configuracoes.cores_lotes'), url: '/configuracoes/cores-lotes' },
        { title: t('layouts.sidebar.configuracoes.parametros'), url: '/configuracoes/parametros' },
        {
          title: t('layouts.sidebar.configuracoes.reservas'),
          url: '/configuracoes/reserva',
        },
        { title: t('layouts.sidebar.configuracoes.perfil'), url: '/configuracoes/perfil' },
        { title: t('layouts.sidebar.configuracoes.termos'), url: '/configuracoes/termos' },
      ],
    },
  ]

  return (
    <SidebarProvider>
      <Sidebar data-testid="app-sidebar" collapsible="icon" {...props}>
        <SidebarHeader>
          <Render.If condition={user}>
            {(_user) => <RegionalSwitcher user={_user} isEmAtendimento={Boolean(atendimento?.id)} />}
          </Render.If>
        </SidebarHeader>
        <SidebarContent>
          <NavMain
            items={[
              ...navMainItems,
              ...(user?.perfilDoUsuario === 'AdministradorDaPlataforma' ? navMainAdminItems : []),
            ]}
          />
        </SidebarContent>
        <SidebarFooter>
          <DestructiveActionGuard
            title={t('login:dialog.logout.title')}
            description={t('login:dialog.logout.description')}
          >
            <SidebarMenuButton className="text-destructive" tooltip={t('layouts.sidebar.sair')} onClick={logout}>
              <Icon name="logout" />
              {t('layouts.sidebar.sair')}
            </SidebarMenuButton>
          </DestructiveActionGuard>
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
      <SidebarInset>
        <Outlet />
      </SidebarInset>
    </SidebarProvider>
  )
}
